// Scope: coordinates the portfolio chat socket session and its room/user workspaces.

import React, { useState, useEffect, useRef } from "react";
import { v4 as uuidv4 } from "uuid";
import { toast } from "react-toastify";
import io from "socket.io-client";
import Chat from "../components/ChatRoom/Chat.js";
import Confirmation from "../components/ChatRoom/Confirmation.js";
import useChatRoomStore from "../chatRoomStore.js";
import CreateRoom from "../components/ChatRoom/CreateRoom.js";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import GroupChat from "../components/ChatRoom/GroupChat.js";
import ProjectPageHeader from "../components/ProjectPageHeader";
import projects from "../assets/projectsData";

const project = projects.find((p) => p.id === "chatroom");
const CHAT_USER_PATTERN = /^(?=.*[a-zA-Z])[a-zA-Z0-9 ]{3,12}$/;

const readSessionJson = (key) => {
    try {
        return JSON.parse(sessionStorage.getItem(key));
    } catch {
        sessionStorage.removeItem(key);
        return null;
    }
};

const ChatRoom = () => {
    const {
        rooms,
        joinedRooms,
        directChats,
        activeDirectChatId,
        openDirectChat,
        setActiveDirectChat,
        appendDirectMessage,
        closeDirectChat,
        unreadCounts,
        incrementUnread,
        clearUnread,
        joinRoomTab,
        clearWorkspaceState,
        createRoom,
        setCreateRoom,
        addRoom,
        setRooms,
        activeRoom,
        deleteRoom,
        setIsPrivateRoom,
        setActiveRoom,
        clearRoomMessages,
    } = useChatRoomStore();

    const [alertMessage, setAlertMessage] = useState(null);
    const [userName, setUserName] = useState("");
    const [active, setActive] = useState(false);
    const [userCount, setUserCount] = useState(0);
    const [chatId, setChatID] = useState(null);
    const [users, setUsers] = useState([]);
    const [connectionState, setConnectionState] = useState("connecting");

    const [incomingRequests, setIncomingRequests] = useState([]);
    const [chatRequestPending, setChatRequestPending] = useState(false);
    const [pendingChatName, setPendingChatName] = useState("");
    const [typingByWorkspace, setTypingByWorkspace] = useState({});
    const [roomPresence, setRoomPresence] = useState({});
    const [unavailableDirectChats, setUnavailableDirectChats] = useState({});
    const [activeWorkspace, setActiveWorkspace] = useState(null);
    const [directoryOpen, setDirectoryOpen] = useState(true);
    const [joinRoomModal, setJoinRoomModal] = useState({ open: false, room: null });
    const [roomPassword, setRoomPassword] = useState("");

    const socket = useRef();
    const chatIdRef = useRef(chatId);
    const roomsRef = useRef(rooms);
    const activeRoomRef = useRef(activeRoom);
    const joinedRoomsRef = useRef(joinedRooms);
    const userNameRef = useRef(userName);
    const hasCleanedUpRef = useRef(false);
    const chatWorkspaceRef = useRef(null);
    const activeWorkspaceRef = useRef(activeWorkspace);
    const myChat = directChats.find((chat) => chat.id === activeDirectChatId) || null;

    useEffect(() => {
        activeWorkspaceRef.current = activeWorkspace;
    }, [activeWorkspace]);

    useEffect(() => {
        if (!activeWorkspace) return;
        window.requestAnimationFrame(() => {
            chatWorkspaceRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
        });
    }, [activeWorkspace]);

    useEffect(() => {
        chatIdRef.current = chatId;
    }, [chatId]);

    useEffect(() => {
        roomsRef.current = rooms;
    }, [rooms]);

    useEffect(() => {
        activeRoomRef.current = activeRoom;
    }, [activeRoom]);

    useEffect(() => {
        joinedRoomsRef.current = joinedRooms;
    }, [joinedRooms]);

    useEffect(() => {
        userNameRef.current = userName;
    }, [userName]);

    const cleanupPresence = () => {
        if (hasCleanedUpRef.current) return;

        if (!socket.current || !chatIdRef.current || !userNameRef.current) {
            return;
        }

        hasCleanedUpRef.current = true;

        joinedRoomsRef.current.forEach((room) => socket.current.emit("leaveRoom", room.id));

        socket.current.emit("removeChatUser");
    };

    useEffect(() => {
        const storedUserData = readSessionJson("userData");

        if (!socket.current) {
            socket.current = io(`${process.env.REACT_APP_BASE_URL || "http://localhost:5500/"}chatroom`, {
                transports: ["websocket"],
                reconnection: true,
                withCredentials: true,
            });
        }

        socket.current.on("connect", () => {
            setConnectionState("connected");
            if (storedUserData) {
                const { chatId, userName } = storedUserData;
                socket.current.emit("newChatUser", { chatId, userName });
                socket.current.emit("map-audit", { chatId });
                hasCleanedUpRef.current = false;
            }

            joinedRoomsRef.current.forEach((room) => socket.current.emit("joinRoom", { roomId: room.id }));
        });

        socket.current.on("disconnect", () => setConnectionState("disconnected"));
        socket.current.on("connect_error", () => {
            setConnectionState("disconnected");
            toast.error("Chat server is unavailable. Reconnecting…");
        });

        if (storedUserData) {
            const { userName, chatId } = storedUserData;
            setUserName(userName);
            setChatID(chatId);
            setActive(true);
        }

        socket.current.on("userUpdate", ({ type, who, users: incomingUsers }) => {
            const currentId = readSessionJson("userData")?.chatId || null;

            if (who?.chatId !== currentId) {
                if (type === "add" && who) {
                    toast.success(`User Joined: ${who.userName}`);
                } else if (type === "remove" && who) {
                    toast.error(`User Left: ${who.userName}`);
                }
            }

            const normalizedUsers = (incomingUsers || [])
                .filter(Boolean)
                .filter((user) => user.chatId !== currentId)
                .filter((user, index, list) => list.findIndex((entry) => entry.chatId === user.chatId) === index);

            setUsers(normalizedUsers);
            setUserCount(normalizedUsers.length);
        });

        socket.current.on("receiveMessage", (messageData) => {
            const peerId = messageData.sender?.id === chatIdRef.current
                ? messageData.receiver
                : messageData.sender?.id;
            if (peerId) appendDirectMessage(peerId, messageData);
            if (peerId && activeWorkspaceRef.current !== `direct:${peerId}`) {
                incrementUnread(`direct:${peerId}`);
            }
        });

        socket.current.on("receiveChatRequest", ({ host, guest }) => {
            setIncomingRequests((requests) => requests.some((request) => request.host.id === host.id)
                ? requests
                : [...requests, { host, guest }]);
        });

        socket.current.on("chatRequestAccept", ({ host, guest }) => {
            setChatRequestPending(false);
            setPendingChatName("");
            openDirectChat(guest || host);
            setUnavailableDirectChats((current) => ({ ...current, [(guest || host).id]: false }));
            setActiveWorkspace(`direct:${(guest || host).id}`);
            toast.success(`${guest?.name || host?.name} accepted your request`);
        });

        socket.current.on("chatRequestDecline", ({ guest }) => {
            setChatRequestPending(false);
            setPendingChatName("");
            toast.warning(`${guest?.name || "The other user"} declined your request.`);
        });

        socket.current.on("chatRequestAccepted", ({ host }) => {
            setChatRequestPending(false);
            setPendingChatName("");
            openDirectChat(host);
            setUnavailableDirectChats((current) => ({ ...current, [host.id]: false }));
            setActiveWorkspace(`direct:${host.id}`);
            toast.success(`Chat started with ${host?.name || "the other user"}`);
        });

        socket.current.on("failedChatRequest", ({ message } = {}) => {
            setChatRequestPending(false);
            setPendingChatName("");
            toast.error(message || "Failed to send chat request");
        });
        socket.current.on("directTyping", ({ senderId, senderName, isTyping } = {}) => {
            if (!senderId) return;
            setTypingByWorkspace((current) => ({
                ...current,
                [`direct:${senderId}`]: isTyping ? `${senderName || "Someone"} is typing…` : "",
            }));
        });
        socket.current.on("roomTyping", ({ roomId, senderName, isTyping } = {}) => {
            if (!roomId) return;
            setTypingByWorkspace((current) => ({
                ...current,
                [`room:${roomId}`]: isTyping ? `${senderName || "Someone"} is typing…` : "",
            }));
        });
        socket.current.on("roomPresence", ({ roomId, count } = {}) => {
            if (roomId) setRoomPresence((current) => ({ ...current, [roomId]: Number(count) || 0 }));
        });

        socket.current.on("chatLeft", ({ partnerId, partnerName } = {}) => {
            if (partnerId) setUnavailableDirectChats((current) => ({ ...current, [partnerId]: true }));
            toast.info(`${partnerName || "The other user"} left the chat`);
        });

        const showSocketError = ({ message } = {}) => {
            toast.error(message || "The chat action could not be completed.");
        };
        socket.current.on("userError", ({ message } = {}) => {
            showSocketError({ message });
            sessionStorage.removeItem("userData");
            setActive(false);
        });
        socket.current.on("messageError", showSocketError);
        socket.current.on("roomError", showSocketError);
        socket.current.on("chatRequestError", showSocketError);
        socket.current.on("chatRequestCancelled", ({ hostId } = {}) => {
            setIncomingRequests((requests) => requests.filter((request) => request.host.id !== hostId));
        });

        //Rooms
        socket.current.on("roomUpdate", (roomsList) => {
            setRooms(roomsList);
        });

        socket.current.on("addRoom", (newRoom) => {
            if (newRoom.hostId !== chatIdRef.current) {
                addRoom(newRoom);
                toast.info(`Room added: ${newRoom.name}`);
            }
        });

        socket.current.on("joinRoomError", ({ message }) => {
            toast.error(message || "Unable to join room.");
        });

        socket.current.on("roomJoined", ({ roomId, roomName }) => {
            const joinedRoom = roomsRef.current.find((room) => room.id === roomId);
            if (joinedRoom) {
                joinRoomTab(joinedRoom);
                setActiveWorkspace(`room:${joinedRoom.id}`);
                closeJoinRoomModal();
                toast.success(`Joined ${roomName}`);
            }
        });

        socket.current.on("roomsClosed", ({ message }) => {
            toast.info(message);
        });

        socket.current.on("deleteRoom", (payload) => {
            const roomId = typeof payload === "object" ? payload.roomId : payload;
            const silent = typeof payload === "object" ? payload.silent : false;

            deleteRoom(roomId);

            if (!silent) {
                toast.info("Room Deleted");
            }
        });

        const handlePageLeave = () => {
            cleanupPresence();
        };

        window.addEventListener("beforeunload", handlePageLeave);
        window.addEventListener("pagehide", handlePageLeave);

        return () => {
            window.removeEventListener("beforeunload", handlePageLeave);
            window.removeEventListener("pagehide", handlePageLeave);
            cleanupPresence();
            if (socket.current) {
                socket.current.removeAllListeners();

                socket.current.disconnect();
                socket.current = null;
            }
        };
    }, [addRoom, appendDirectMessage, closeDirectChat, deleteRoom, incrementUnread, joinRoomTab, openDirectChat, setRooms]);

    const handleFormSubmit = (e) => {
        e.preventDefault();

        const username = userName.trim();

        if (!CHAT_USER_PATTERN.test(username)) {
            setAlertMessage(
                "Username must be between 3 and 12 characters and include at least one alphabet character."
            );
            return;
        }
        const newChatId = uuidv4();
        const userData = JSON.stringify({
            userName: username,
            chatId: newChatId,
        });
        sessionStorage.setItem("userData", userData);

        setChatID(newChatId);
        setUserName(username);
        setActiveRoom(null);
        clearRoomMessages();
        setCreateRoom(false);
        setIsPrivateRoom(false);

        setActive(true);
        hasCleanedUpRef.current = false;
        socket.current?.emit("newChatUser", {
            chatId: newChatId,
            userName: username,
        });
    };

    const handleClearUser = () => {
        if (socket.current && chatId && userName) {
            cleanupPresence();
        }

        setActive(false);
        setUsers([]);
        setUserCount(0);
        setChatRequestPending(false);
        clearWorkspaceState();
        setActiveWorkspace(null);
        setAlertMessage(null);
        sessionStorage.removeItem("userData");
        clearRoomMessages();
        setActiveRoom(null);
        setCreateRoom(false);
        setIsPrivateRoom(false);
    };

    const handleJoinChat = ({ guestId, guestName }) => {
        if (!socket.current?.connected || chatRequestPending) return;
        socket.current.emit("sendChatRequest", {
            guest: { id: guestId, name: guestName },
        });
        setChatRequestPending(true);
        setPendingChatName(guestName);
    };

    const handleConfirmation = () => {
        setIncomingRequests((requests) => requests.slice(1));
        setChatRequestPending(false);
    };

    const cancelOutgoingRequest = () => {
        socket.current?.emit("cancelChatRequest");
        setChatRequestPending(false);
        setPendingChatName("");
    };

    const selectWorkspace = (workspace, directChatId = null) => {
        setActiveWorkspace(workspace);
        if (directChatId) setActiveDirectChat(directChatId);
        if (workspace.startsWith("room:")) {
            const roomId = workspace.slice(5);
            setActiveRoom(joinedRooms.find((room) => room.id === roomId) || null);
        }
        clearUnread(workspace);
        setDirectoryOpen(false);
    };

    const handleCloseDirectChat = (directChatId) => {
        const remainingChat = directChats.filter((chat) => chat.id !== directChatId).at(-1);
        closeDirectChat(directChatId);
        if (activeWorkspace === `direct:${directChatId}`) {
            setActiveWorkspace(remainingChat ? `direct:${remainingChat.id}` : activeRoom ? `room:${activeRoom.id}` : null);
        }
    };

    const handleCreateOpenRoom = () => {
        setCreateRoom(true);
    };

    const handleCreatePrivateRoom = () => {
        setCreateRoom(true);
        setIsPrivateRoom(true);
    };

    const handleDeleteRoom = (roomId) => {
        const room = rooms.find((item) => item.id === roomId);
        if (!window.confirm(`Close “${room?.name || "this room"}” for everyone? This cannot be undone.`)) return;
        socket.current.emit("deleteRoom", roomId);
    };

    const handleJoinRoom = (room) => {
        if (room.isPrivate) {
            setJoinRoomModal({ open: true, room });
            setRoomPassword("");
            return;
        }

        socket.current.emit("joinRoom", { roomId: room.id });
    };

    const closeJoinRoomModal = () => {
        setJoinRoomModal({ open: false, room: null });
        setRoomPassword("");
    };

    const handleRoomPasswordSubmit = (e) => {
        e.preventDefault();

        const room = joinRoomModal.room;
        const password = roomPassword.trim();

        if (!room) return;

        if (!password) {
            setAlertMessage("A password is required to join this private room.");
            return;
        }

        socket.current.emit("joinRoom", {
            roomId: room.id,
            roomKey: password,
        });
    };

    const clearAlert = () => {
        setAlertMessage(null);
    };

    return (
        <div className="chat-app">
            <ProjectPageHeader
                title={project.title}
                description={project.description}
                tech={project.tech}
            />
            <details className="chat-architecture">
                <summary>How this live demo works</summary>
                <div className="chat-architecture__content">
                    <p>Socket.IO provides temporary presence, direct conversations, and public or password-protected rooms.</p>
                    <ul>
                        <li>Identity and message senders are enforced by the server.</li>
                        <li>Inputs and action rates are bounded for a public portfolio demo.</li>
                        <li>Messages are ephemeral and disappear when the free-tier server restarts.</li>
                    </ul>
                    <a href="https://github.com/bhattsahil95/MERN-Cleint" target="_blank" rel="noreferrer">View source code →</a>
                </div>
            </details>
            {alertMessage && (
                <div className="alert-message" role="alert">
                    {alertMessage}
                    <button className="" onClick={clearAlert}>
                        close
                    </button>
                </div>
            )}
            <div className={`chat-container${activeWorkspace ? " has-workspace" : ""}`}>
                {chatRequestPending && (
                    <div className="request-pending" role="status" aria-live="polite">
                        <span className="request-pending__spinner" aria-hidden="true" />
                        <div>
                            <strong>Waiting for {pendingChatName || "response"}</strong>
                            <span>Your invitation has been sent.</span>
                        </div>
                        <button type="button" className="request-pending__cancel" onClick={cancelOutgoingRequest}>
                            Cancel
                        </button>
                    </div>
                )}

                <div>
                    {!active ? (
                        <form
                            className="username-form"
                            onSubmit={handleFormSubmit}
                        >
                            <h2>Join the live demo</h2>
                            <p className="chat-form-intro">
                                Choose a temporary display name. Messages and rooms are not retained after the server restarts.
                            </p>
                            <label className="username-label" htmlFor="chat-user-name">
                                Display name
                                <input
                                    id="chat-user-name"
                                    className="username-input"
                                    type="text"
                                    onChange={(e) =>
                                        setUserName(e.target.value)
                                    }
                                    value={userName}
                                    minLength={3}
                                    maxLength={12}
                                    autoComplete="off"
                                    autoFocus
                                    required
                                />
                            </label>
                            <button
                                className="username-submit-btn"
                                type="submit"
                            >
                                Join chat
                            </button>
                        </form>
                    ) : (
                        <div className={`chat-directory${directoryOpen ? " is-open" : ""}`}>
                            <div className="user-info">
                                <span className={`connection-status ${connectionState}`}>
                                    {connectionState === "connected" ? "Connected" : "Reconnecting…"}
                                </span>
                                <span>Others online: {userCount}</span>
                                <span>Signed in as: {userName}</span>
                               <div className="renew-button">
                                    <button type="button" onClick={handleClearUser}>
                                        Change identity
                                    </button>
                                </div>
                            </div>

                            <div className="create-rooms">
                                <div className="create-options">
                                    <button
                                        onClick={handleCreateOpenRoom}
                                        className="create-btn open"
                                        disabled={connectionState !== "connected"}
                                    >
                                        Create Open Room
                                    </button>
                                    <button
                                        onClick={handleCreatePrivateRoom}
                                        className="create-btn private"
                                        disabled={connectionState !== "connected"}
                                    >
                                        Create Private Room
                                    </button>
                                </div>
                            </div>

                            <div className="room-container">
                                <div className="existing-rooms room-card">
                                    <h2>Existing Rooms</h2>
                                    <div className="room-list">
                                        {rooms.length === 0 && (
                                            <p className="chat-empty-state">No rooms yet. Create one to start a group conversation.</p>
                                        )}
                                        {rooms.map((room) => (
                                            <div
                                                key={room.id}
                                                className={`room ${
                                                    room.isPrivate
                                                        ? "private-room"
                                                        : "open-room"
                                                }`}
                                            >
                                                <div className="room-info">
                                                    <span className="room-name">
                                                        {room.name}
                                                        {room.hostId === chatId && <small className="room-owned-badge">Yours</small>}
                                                    </span>
                                                    <span className="room-id">
                                                        Room ID: {room.id}
                                                    </span>
                                                </div>
                                                <div className="lock-and-button-container">
                                                    {room.isPrivate && (
                                                        <div className="room-lock">
                                                            <LockOutlinedIcon />
                                                        </div>
                                                    )}
                                                    {room.hostId === chatId && (
                                                        <button
                                                            type="button"
                                                            className="room-remove"
                                                            aria-label="Delete room"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleDeleteRoom(room.id);
                                                            }}
                                                        >
                                                            <DeleteOutlineOutlinedIcon fontSize="small" />
                                                        </button>
                                                    )}
                                                    <button
                                                        className="join-btn"
                                                        disabled={connectionState !== "connected"}
                                                        onClick={() =>
                                                            handleJoinRoom(room)
                                                        }
                                                    >
                                                        Join
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                <div className="existing-rooms room-card">
                                    <h2>Existing Users</h2>
                                    <div className="user-list">
                                        {users.length === 0 && (
                                            <p className="chat-empty-state">No one else is online right now.</p>
                                        )}
                                        {users.map((user) => {
                                            if (!user?.chatId || user.chatId === chatId) {
                                                return null;
                                            }

                                            return (
                                                <div
                                                    key={user.chatId}
                                                    className="room current-users"
                                                >
                                                    <span>{user.userName}</span>
                                                    <button
                                                        className="join-btn"
                                                        disabled={chatRequestPending || connectionState !== "connected"}
                                                        onClick={() =>
                                                            handleJoinChat({
                                                                guestId: user.chatId,
                                                                guestName: user.userName,
                                                            })
                                                        }
                                                    >
                                                        Chat
                                                    </button>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
                {(directChats.length > 0 || joinedRooms.length > 0) && (
                    <section className="direct-chat-workspace" ref={chatWorkspaceRef} aria-label="Open conversations">
                        <div className="direct-chat-tabs" role="tablist" aria-label="Open conversations">
                            <span className="direct-chat-tabs__label">Conversations</span>
                            <div className="direct-chat-tabs__scroll">
                                {directChats.map((chat) => {
                                    return (
                                        <button
                                            key={chat.id}
                                            type="button"
                                            role="tab"
                                            aria-selected={activeWorkspace === `direct:${chat.id}`}
                                            className={`direct-chat-tab${activeWorkspace === `direct:${chat.id}` ? " active" : ""}`}
                                            onClick={() => selectWorkspace(`direct:${chat.id}`, chat.id)}
                                        >
                                            <span className="direct-chat-tab__presence" aria-hidden="true" />
                                            <span>{chat.name}</span>
                                            {(unreadCounts[`direct:${chat.id}`] || 0) > 0 && (
                                                <span className="direct-chat-tab__count" aria-label={`${unreadCounts[`direct:${chat.id}`]} unread messages`}>
                                                    {Math.min(unreadCounts[`direct:${chat.id}`], 99)}
                                                </span>
                                            )}
                                        </button>
                                    );
                                })}
                                {joinedRooms.map((room) => (
                                    <button
                                        key={room.id}
                                        type="button"
                                        role="tab"
                                        aria-selected={activeWorkspace === `room:${room.id}`}
                                        className={`direct-chat-tab room-tab${activeWorkspace === `room:${room.id}` ? " active" : ""}`}
                                        onClick={() => selectWorkspace(`room:${room.id}`)}
                                    >
                                        <span className="direct-chat-tab__presence" aria-hidden="true" />
                                        <span># {room.name}</span>
                                        <span className="direct-chat-tab__meta">{roomPresence[room.id] || 1}</span>
                                        {(unreadCounts[`room:${room.id}`] || 0) > 0 && (
                                            <span className="direct-chat-tab__count" aria-label={`${unreadCounts[`room:${room.id}`]} unread messages`}>
                                                {Math.min(unreadCounts[`room:${room.id}`], 99)}
                                            </span>
                                        )}
                                    </button>
                                ))}
                            </div>
                        </div>
                        {activeWorkspace?.startsWith("direct:") && (
                        <div className="display-chat conversation-panel">
                            <Chat
                                myChat={myChat}
                                socket={socket.current}
                                chatId={chatId}
                                onClose={handleCloseDirectChat}
                                connectionState={connectionState}
                                typingText={typingByWorkspace[`direct:${myChat?.id}`]}
                                unavailable={Boolean(unavailableDirectChats[myChat?.id])}
                                onReconnect={() => handleJoinChat({ guestId: myChat.id, guestName: myChat.name })}
                            />
                        </div>
                        )}
                        <div className="display-chat">
                            <GroupChat
                                socket={socket.current}
                                chatId={chatId}
                                visible={activeWorkspace === `room:${activeRoom?.id}`}
                                onIncoming={(message) => {
                                    const workspace = `room:${message.roomId}`;
                                    if (activeWorkspaceRef.current !== workspace) {
                                        incrementUnread(workspace);
                                    }
                                }}
                                connectionState={connectionState}
                                typingText={typingByWorkspace[`room:${activeRoom?.id}`]}
                                onClose={() => {
                                    const fallback = directChats.at(-1);
                                    setActiveWorkspace(fallback ? `direct:${fallback.id}` : null);
                                }}
                            />
                        </div>
                    </section>
                )}
                {active && activeWorkspace && (
                    <button type="button" className="chat-directory-toggle" onClick={() => setDirectoryOpen((value) => !value)}>
                        {directoryOpen ? "Back to conversation" : "Browse rooms & people"}
                    </button>
                )}
                <div>
                    {createRoom && (
                        <CreateRoom
                            socket={socket.current}
                            chatId={chatId}
                        ></CreateRoom>
                    )}
                </div>
            </div>
            {incomingRequests.length > 0 && (
                <Confirmation
                    host={incomingRequests[0].host}
                    socket={socket.current}
                    handleAnswer={handleConfirmation}
                    guest={incomingRequests[0].guest}
                    queuedCount={incomingRequests.length - 1}
                ></Confirmation>
            )}

            {joinRoomModal.open && joinRoomModal.room && (
                <div className="room-password-modal-overlay" onMouseDown={closeJoinRoomModal}>
                    <div
                        className="room-password-modal"
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="room-password-title"
                        onMouseDown={(event) => event.stopPropagation()}
                    >
                        <h3 id="room-password-title">Enter room password</h3>
                        <p>{joinRoomModal.room.name}</p>
                        <input
                            className="room-password-input"
                            type="password"
                            placeholder="Password"
                            value={roomPassword}
                            minLength={4}
                            maxLength={64}
                            autoComplete="current-password"
                            autoFocus
                            onChange={(e) => setRoomPassword(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                    handleRoomPasswordSubmit(e);
                                }
                            }}
                        />
                        <div className="room-password-modal-actions">
                            <button
                                type="button"
                                className="room-password-cancel"
                                onClick={closeJoinRoomModal}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="room-password-submit"
                                onClick={handleRoomPasswordSubmit}
                            >
                                Join room
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ChatRoom;
